import { isPublicPath } from "./public-paths";
import { hardNavigation } from "./navigation";

/**
 * SSOT для флоу «вернуться туда, откуда пришёл» через sessionStorage.
 *
 * Писатели: `SsoLoginButton` (перед редиректом на IdP), `PublicEventRegistrationPage`
 * (неавторизованный гость уходит на /login), `EventGuestJoinPage` («Log in to
 * Register»), три 401-клиента через `redirectToLoginPreservingLocation`
 * (http-client.ts, services/api/api.ts, services/api.ts). Читатели: `LoginForm`
 * (обычный вход), `SsoCallbackPage` (возврат от IdP), `RegistrationSuccessPage`
 * («Продолжить» после свежей регистрации).
 *
 * sessionStorage — граница доверия (значение мог записать любой код/расширение):
 * значение принимается ТОЛЬКО как внутренний путь приложения — начинается с
 * одиночного «/» (не «//» и не «/\», которые браузер трактует как
 * протокол-относительный URL) — иначе отбрасывается (анти open-redirect).
 */
const RETURN_URL_KEY = "returnUrl";

/** Внутренний путь приложения: «/…», но не «//…» и не «/\…». */
export function isSafeInternalPath(path: string): boolean {
  return /^\/(?![/\\])/.test(path);
}

/** Запомнить путь возврата; небезопасные значения молча не сохраняются. */
export function rememberReturnUrl(path: string): void {
  if (isSafeInternalPath(path)) {
    sessionStorage.setItem(RETURN_URL_KEY, path);
  }
}

/**
 * Посмотреть сохранённый путь возврата БЕЗ потребления (для писателей,
 * которые не должны перезатирать ранее сохранённое место — см. SsoLoginButton).
 * Небезопасное значение трактуется как отсутствующее (но не удаляется —
 * удаление остаётся за consumeReturnUrl).
 */
export function peekReturnUrl(): string | null {
  const value = sessionStorage.getItem(RETURN_URL_KEY);
  return value !== null && isSafeInternalPath(value) ? value : null;
}

/**
 * Прочитать и удалить путь возврата (одноразовый). Небезопасное значение
 * удаляется и трактуется как отсутствующее.
 */
export function consumeReturnUrl(): string | null {
  const value = sessionStorage.getItem(RETURN_URL_KEY);
  if (value === null) {
    return null;
  }
  sessionStorage.removeItem(RETURN_URL_KEY);
  return isSafeInternalPath(value) ? value : null;
}

/**
 * DRY-хелпер для 401-интерсепторов: запомнить текущее место (pathname+search)
 * и увести на /auth/login. С публичных страниц (см. public-paths.ts, включая
 * все /auth/*) НЕ уводит и ничего не пишет: бутстрап-401 гостя — штатная
 * ситуация, страница остаётся. Заменяет мёртвый write-only `?redirect=`
 * (читателей у него не было).
 */
export function redirectToLoginPreservingLocation(): void {
  const { pathname, search } = window.location;
  if (isPublicPath(pathname)) {
    return;
  }
  rememberReturnUrl(pathname + search);
  hardNavigation.assign("/auth/login");
}
